import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../api";
import ReactGA from "react-ga4";

const inputStyle = {
  width: "100%", height: "40px",
  padding: "0 12px", borderRadius: "9px",
  border: "1px solid #e5e7eb", outline: "none",
  fontSize: "13px", color: "#374151",
  transition: "border-color 0.15s, box-shadow 0.15s",
  boxSizing: "border-box",
};

const labelStyle = {
  display: "block", fontSize: "11px", fontWeight: 700,
  color: "#9ca3af", textTransform: "uppercase",
  letterSpacing: "0.06em", marginBottom: "5px",
};

const onFocus = (e) => {
  e.currentTarget.style.borderColor = "#a5b4fc";
  e.currentTarget.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.12)";
};
const onBlur = (e) => {
  e.currentTarget.style.borderColor = "#e5e7eb";
  e.currentTarget.style.boxShadow = "none";
};
const onFocusInvalid = (e) => {
  e.currentTarget.style.borderColor = "#fca5a5";
  e.currentTarget.style.boxShadow = "0 0 0 3px rgba(239,68,68,0.1)";
};

const GoogleIcon = () => (
  <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

const SignupForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const selectedPlanId = location.state?.selectedPlanId;
  const signupSource = new URLSearchParams(location.search).get("from") || "direct";

  const [step, setStep] = useState("signup");
  const [formData, setFormData] = useState({
    firstname: "", lastname: "", email: "", password: "", confirm_password: "",
  });
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
  const passwordsMatch = formData.password && formData.password === formData.confirm_password;
  const passwordStrong = formData.password.length >= 6;
  const isFormValid = formData.firstname && formData.lastname && isEmailValid && passwordsMatch && passwordStrong;

  const handleGoogleSignup = () => {
    window.location.href = `${api.defaults.baseURL}/auth/google`;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;
    setError("");
    try {
      setLoading(true);
      const res = await api.post("/auth/signup", formData);
      if (res.data.success) {
        setStep("verify");
        setResendTimer(60);
      } else {
        setError(res.data.message || t("signup_page.signup_error"));
      }
    } catch (err) {
      setError(err.response?.data?.detail || t("signup_page.signup_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setError("");
    if (!code || code.length < 6) { setError(t("signup_page.code_required")); return; }
    try {
      setLoading(true);
      const res = await api.post("/auth/verify-email", { email: formData.email, code });
      if (res.data.verified) {
        ReactGA.event({ category: "conversion", action: "sign_up", label: signupSource });
        setStep("success");
        if (selectedPlanId && selectedPlanId > 1) navigate("/checkout", { state: { selectedPlanId } });
      } else {
        setError(t("signup_page.invalid_code"));
      }
    } catch (err) {
      setError(err.response?.data?.detail || t("signup_page.verify_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setError("");
    try {
      setLoading(true);
      const res = await api.post("/auth/signup/resend-code", { email: formData.email });
      if (res.data.success) setResendTimer(60);
      else setError(t("signup_page.resend_error"));
    } catch (err) {
      setError(err.response?.data?.detail || t("signup_page.resend_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  return (
    <div style={{ minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px" }}>
      <div style={{ width: "100%", maxWidth: "420px" }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <img src="/logo_central.png" alt="Pic-Loud Logo" style={{ height: "48px", width: "auto" }} />
        </div>

        {/* Card */}
        <div style={{
          background: "#fff", borderRadius: "18px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
          padding: "32px 28px",
        }}>

          {/* ── Step 1: Signup ── */}
          {step === "signup" && (
            <>
              <h5 style={{ textAlign: "center", fontWeight: 700, color: "#111827", marginBottom: "24px", fontSize: "17px" }}>
                {t("signup_page.title")}
              </h5>

              {error && (
                <div style={{ padding: "10px 14px", borderRadius: "9px", background: "#fff5f5", border: "1px solid #fecaca", color: "#991b1b", fontSize: "13px", marginBottom: "16px" }}>
                  {error}
                </div>
              )}

              {/* Google button */}
              <button
                type="button"
                onClick={handleGoogleSignup}
                style={{
                  width: "100%", height: "44px",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
                  borderRadius: "10px", border: "1px solid #e5e7eb",
                  background: "#fff", cursor: "pointer",
                  fontSize: "14px", fontWeight: 500, color: "#374151",
                  transition: "all 0.15s", marginBottom: "20px",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "#f9fafb"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.07)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.boxShadow = "none"; }}
              >
                <GoogleIcon />
                {t("signup_page.google_button")}
              </button>

              {/* Divider */}
              <div style={{ position: "relative", marginBottom: "20px" }}>
                <div style={{ height: 1, background: "#f3f4f6" }} />
                <span style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", background: "#fff", padding: "0 10px", fontSize: "12px", color: "#9ca3af" }}>
                  {t("signup_page.or_text")}
                </span>
              </div>

              <form onSubmit={handleSignup}>
                {/* First + Last name row */}
                <div style={{ display: "flex", gap: "12px", marginBottom: "14px" }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>{t("signup_page.firstname_label")}</label>
                    <input
                      type="text" name="firstname" value={formData.firstname}
                      onChange={handleChange} required
                      style={inputStyle} onFocus={onFocus} onBlur={onBlur}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>{t("signup_page.lastname_label")}</label>
                    <input
                      type="text" name="lastname" value={formData.lastname}
                      onChange={handleChange} required
                      style={inputStyle} onFocus={onFocus} onBlur={onBlur}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: "14px" }}>
                  <label style={labelStyle}>{t("signup_page.email_label")}</label>
                  <input
                    type="email" name="email" value={formData.email}
                    onChange={handleChange} required
                    style={{
                      ...inputStyle,
                      borderColor: formData.email && !isEmailValid ? "#fca5a5" : "#e5e7eb",
                    }}
                    onFocus={formData.email && !isEmailValid ? onFocusInvalid : onFocus}
                    onBlur={onBlur}
                  />
                  {formData.email && !isEmailValid && (
                    <p style={{ fontSize: "11px", color: "#ef4444", marginTop: "4px", marginBottom: 0 }}>
                      {t("signup_page.invalid_email")}
                    </p>
                  )}
                </div>

                <div style={{ marginBottom: "14px" }}>
                  <label style={labelStyle}>{t("signup_page.password_label")}</label>
                  <input
                    type="password" name="password" value={formData.password}
                    onChange={handleChange} required
                    style={{
                      ...inputStyle,
                      borderColor: formData.password && !passwordStrong ? "#fca5a5" : "#e5e7eb",
                    }}
                    onFocus={formData.password && !passwordStrong ? onFocusInvalid : onFocus}
                    onBlur={onBlur}
                  />
                  <p style={{ fontSize: "11px", marginTop: "4px", marginBottom: 0, color: passwordStrong ? "#16a34a" : "#9ca3af" }}>
                    {passwordStrong ? t("signup_page.password_strong") : t("signup_page.password_hint")}
                  </p>
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label style={labelStyle}>{t("signup_page.confirm_password_label")}</label>
                  <input
                    type="password" name="confirm_password" value={formData.confirm_password}
                    onChange={handleChange} required
                    style={{
                      ...inputStyle,
                      borderColor: formData.confirm_password && !passwordsMatch ? "#fca5a5" : "#e5e7eb",
                    }}
                    onFocus={formData.confirm_password && !passwordsMatch ? onFocusInvalid : onFocus}
                    onBlur={onBlur}
                  />
                  {formData.confirm_password && !passwordsMatch && (
                    <p style={{ fontSize: "11px", color: "#ef4444", marginTop: "4px", marginBottom: 0 }}>
                      {t("signup_page.passwords_mismatch")}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!isFormValid || loading}
                  style={{
                    width: "100%", height: "44px",
                    borderRadius: "10px", border: "none",
                    background: !isFormValid || loading ? "#a5b4fc" : "#6366f1",
                    color: "#fff", fontSize: "14px", fontWeight: 600,
                    cursor: !isFormValid || loading ? "not-allowed" : "pointer",
                    transition: "background 0.15s",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                  onMouseEnter={e => { if (isFormValid && !loading) e.currentTarget.style.background = "#4f46e5"; }}
                  onMouseLeave={e => { if (isFormValid && !loading) e.currentTarget.style.background = "#6366f1"; }}
                >
                  {loading ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ animation: "spin 1s linear infinite" }}>
                      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3"/>
                      <path d="M12 2a10 10 0 0110 10" stroke="#fff" strokeWidth="3" strokeLinecap="round"/>
                      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                    </svg>
                  ) : t("signup_page.signup_button")}
                </button>
              </form>

              <div style={{ textAlign: "center", marginTop: "20px", fontSize: "13px", color: "#6b7280" }}>
                {t("signup_page.login_prompt")}{" "}
                <Link
                  to="/login"
                  state={{ selectedPlanId }}
                  style={{ color: "#6366f1", fontWeight: 600, textDecoration: "none" }}
                  onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
                  onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}
                >
                  {t("signup_page.login_link")}
                </Link>
              </div>
            </>
          )}

          {/* ── Step 2: Verify ── */}
          {step === "verify" && (
            <>
              <h5 style={{ textAlign: "center", fontWeight: 700, color: "#111827", marginBottom: "12px", fontSize: "17px" }}>
                {t("signup_page.verify_title")}
              </h5>
              <p style={{ textAlign: "center", fontSize: "13px", color: "#6b7280", marginBottom: "20px" }}>
                {t("signup_page.verify_desc", { email: formData.email })}
              </p>

              {error && (
                <div style={{ padding: "10px 14px", borderRadius: "9px", background: "#fff5f5", border: "1px solid #fecaca", color: "#991b1b", fontSize: "13px", marginBottom: "16px" }}>
                  {error}
                </div>
              )}

              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t("signup_page.verify_placeholder")}
                maxLength={6}
                style={{
                  ...inputStyle,
                  textAlign: "center", letterSpacing: "8px",
                  fontSize: "22px", height: "52px",
                  marginBottom: "16px",
                }}
                onFocus={onFocus}
                onBlur={onBlur}
              />

              <button
                onClick={handleVerify}
                disabled={loading || code.length < 6}
                style={{
                  width: "100%", height: "44px",
                  borderRadius: "10px", border: "none",
                  background: loading || code.length < 6 ? "#a5b4fc" : "#6366f1",
                  color: "#fff", fontSize: "14px", fontWeight: 600,
                  cursor: loading || code.length < 6 ? "not-allowed" : "pointer",
                  marginBottom: "12px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
                onMouseEnter={e => { if (!loading && code.length >= 6) e.currentTarget.style.background = "#4f46e5"; }}
                onMouseLeave={e => { if (!loading && code.length >= 6) e.currentTarget.style.background = "#6366f1"; }}
              >
                {loading ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ animation: "spin 1s linear infinite" }}>
                    <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3"/>
                    <path d="M12 2a10 10 0 0110 10" stroke="#fff" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                ) : t("signup_page.verify_button")}
              </button>

              <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
                <button
                  onClick={handleResend}
                  disabled={resendTimer > 0 || loading}
                  style={{
                    background: "none", border: "none", cursor: resendTimer > 0 ? "default" : "pointer",
                    fontSize: "13px", color: resendTimer > 0 ? "#9ca3af" : "#6366f1", padding: 0,
                  }}
                >
                  {resendTimer > 0 ? t("signup_page.resend_timer", { count: resendTimer }) : t("signup_page.resend_button")}
                </button>
                <span style={{ color: "#e5e7eb" }}>|</span>
                <button
                  onClick={() => setStep("signup")}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: "13px", color: "#6b7280", padding: 0 }}
                >
                  {t("signup_page.change_email")}
                </button>
              </div>

              <p style={{ textAlign: "center", fontSize: "12px", color: "#9ca3af", marginTop: "16px", marginBottom: 0 }}>
                {t("signup_page.spam_hint")}
              </p>
            </>
          )}

          {/* ── Step 3: Success ── */}
          {step === "success" && (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "48px", marginBottom: "12px" }}>✅</div>
              <h5 style={{ fontWeight: 700, color: "#111827", marginBottom: "8px", fontSize: "17px" }}>
                {t("signup_page.success_title")}
              </h5>
              <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "24px" }}>
                {t("signup_page.success_desc")}
              </p>
              <button
                onClick={() => navigate("/login")}
                style={{
                  width: "100%", height: "44px",
                  borderRadius: "10px", border: "none",
                  background: "#6366f1", color: "#fff",
                  fontSize: "14px", fontWeight: 600, cursor: "pointer",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#4f46e5"}
                onMouseLeave={e => e.currentTarget.style.background = "#6366f1"}
              >
                {t("signup_page.go_to_login")}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default SignupForm;
